/* eslint-disable no-await-in-loop -- Each long-poll request must finish before starting the next one. */
import { hashQueryKey, onlineManager, QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useOnlineStatus } from 'hooks/useOnlineStatus';

type UseLongPollingQueryOptions<TData = unknown> = {
	enabled?: boolean;
	onMessage?: (data: TData) => void;
};

export const useLongPollingQuery = <
	TQueryFnData = unknown,
	TError extends AxiosError<any> = AxiosError<any>,
	TData = TQueryFnData,
	TQueryKey extends QueryKey = QueryKey,
>(
	queryKey: TQueryKey,
	queryFn: ({ params, signal }: { params: any; page?: any; signal: AbortSignal }) => Promise<TQueryFnData>,
	options?: UseLongPollingQueryOptions<TData>
) => {
	const queryClient = useQueryClient();
	const isOnline = useOnlineStatus();
	const enabled = options?.enabled !== false && isOnline;
	const queryHash = hashQueryKey(queryKey);
	// Callers create query-key arrays on every render; only restart for a changed key.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const stableQueryKey = useMemo(() => queryKey, [queryHash]);
	const onMessageRef = useRef(options?.onMessage);
	onMessageRef.current = options?.onMessage;
	const stopPollingRef = useRef<() => void>(() => {});

	const { data, refetch } = useQuery<TQueryFnData, TError, TData, TQueryKey>(
		stableQueryKey,
		({ pageParam, queryKey, signal }) => queryFn({ params: queryKey[1], page: pageParam, signal: signal! }),
		{ enabled: false, retry: false }
	);

	useEffect(() => {
		if (!enabled) return undefined;
		let stopped = false;
		let failures = 0;
		let timer: ReturnType<typeof setTimeout> | undefined;
		let finishDelay: (() => void) | undefined;
		const stop = () => {
			stopped = true;
			clearTimeout(timer);
			finishDelay?.();
			// Abort the outstanding subscription without discarding cached messages.
			queryClient.cancelQueries({ queryKey: stableQueryKey, exact: true });
		};
		stopPollingRef.current = stop;
		const delay = (ms: number) =>
			new Promise<void>((resolve) => {
				finishDelay = resolve;
				timer = setTimeout(resolve, ms);
			});
		const poll = async () => {
			while (!stopped && onlineManager.isOnline()) {
				const response = await refetch({ throwOnError: false });
				if (stopped || !onlineManager.isOnline()) return;
				if (response.isError) {
					failures += 1;
					await delay(Math.min(1000 * 2 ** (failures - 1), 30000));
				} else {
					failures = 0;
					if (response.data) onMessageRef.current?.(response.data);
					// Avoid a tight loop if the server immediately returns an empty response.
					await delay(response.data ? 0 : 250);
				}
			}
		};
		poll().catch(stop);
		return stop;
	}, [enabled, queryClient, refetch, stableQueryKey]);

	const cancel = useCallback(() => stopPollingRef.current(), []);
	return { data: data === '' ? null : data, cancel };
};
